/**
 * Wspólne helpery dat/timezone.
 *
 * Tło: `Date.toISOString()` zawsze zwraca UTC ("Z" suffix), gubiąc lokalny offset.
 * Backend potrzebuje lokalnego offsetu w `reviewedAt`, żeby `DailyStats` kubełkować po
 * dacie lokalnej użytkownika (a nie UTC) — inaczej seria nauki resetuje się dla user'ów
 * studiujących wieczorem/nocą w strefach != UTC (np. Polska UTC+2 — Polski wieczór 22-23
 * to ten sam dzień UTC, ale nauka 00-02 Polish ląduje w POPRZEDNIM dniu UTC → dziura).
 */

/** Aktualny offset użytkownika w minutach NA WSCHÓD od UTC (np. CEST → +120). */
export function localTzOffsetMinutes(): number {
  return -new Date().getTimezoneOffset();
}

/**
 * Formatuje `Date` jako ISO 8601 z jawnym lokalnym offsetem (zamiast UTC `Z`).
 *
 * Przykład: `Date(2026-05-25T01:30:00Z)` + offset `+120` →
 * `"2026-05-25T03:30:00.000+02:00"` — backend `DateTimeOffset.DateTime` daje lokalną
 * datę 2026-05-25 (Polish today), zamiast UTC 2026-05-24.
 *
 * @param date — moment do sformatowania.
 * @param tzOffsetMinutes — offset NA WSCHÓD od UTC (default: bieżący lokalny).
 */
export function toLocalIsoString(
  date: Date,
  tzOffsetMinutes: number = localTzOffsetMinutes(),
): string {
  // Trick: dodajemy offset do epochu w ms → przesunięty moment, którego UTC-pola odpowiadają
  // lokalnemu zegarowi w docelowej strefie. Doklejamy formatted offset suffix.
  const shifted = new Date(date.getTime() + tzOffsetMinutes * 60_000);
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const pad3 = (n: number) => String(n).padStart(3, '0');
  const abs = Math.abs(tzOffsetMinutes);
  const sign = tzOffsetMinutes >= 0 ? '+' : '-';
  return (
    `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}` +
    `T${pad2(shifted.getUTCHours())}:${pad2(shifted.getUTCMinutes())}:${pad2(shifted.getUTCSeconds())}` +
    `.${pad3(shifted.getUTCMilliseconds())}` +
    `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`
  );
}
